import {
  IntegrationEnhanced,
  useCreateIntegrationConnection,
} from "@/api/integrations";
import {
  usePostPipedreamConnectOrgToken,
  usePostPipedreamConnectUserToken,
} from "@/api/integrations/pipedream";
import Sentry from "@/integrations/Sentry";
import analytics from "@/utils/analytics";
import { IntegrationConnection } from "@cohesive/api-client";

import { createFrontendClient } from "@pipedream/sdk/browser";
import { useMutation } from "@tanstack/react-query";

export function useConnectPipedream({
  autoClose = true,
}: {
  autoClose?: boolean;
} = {}) {
  const pipedreamUserMutation = usePostPipedreamConnectUserToken();
  const pipedreamOrgMutation = usePostPipedreamConnectOrgToken();

  const createIntegrationConnection = useCreateIntegrationConnection();

  const connectPipedreamMutation = useMutation<
    IntegrationConnection | { cancelled: boolean },
    Error,
    IntegrationEnhanced
  >({
    mutationFn: async (integration: IntegrationEnhanced) => {
      const createConnection =
        integration.auth_level === "organization"
          ? pipedreamOrgMutation
          : pipedreamUserMutation;
      const response = await createConnection.mutateAsync();

      if (!response.data.data.token) {
        throw new Error("No token received from Pipedream");
      }

      const pd = createFrontendClient();

      return new Promise((resolve, reject) => {
        pd.connectAccount({
          app: integration.platform,
          token: response.data.data.token,
          oauthAppId: integration.integration_provider_oauth_app_id,
          onSuccess: async (account: any) => {
            try {
              const newConnection =
                await createIntegrationConnection.mutateAsync({
                  provider: integration.provider,
                  providerId: account.id,
                  createdAt: integration.created_at,
                  integration: integration,
                });

              analytics.trackUserConnectionAuthorized({
                integrationId: integration.id,
                platform: integration.platform,
                provider: "pipedream",
              });

              if (autoClose) {
                // @ts-expect-error - cleanup is a private method but we need to call it to close the iframe
                pd.cleanup();
              }

              resolve(newConnection);
            } catch (error) {
              reject(error);
            }
          },
          onError: (err: Error) => {
            Sentry.captureException(err, {
              extra: {
                integration,
              },
            });
            reject(new Error(`Pipedream connection failed: ${err.message}`));
          },
          onClose: (status: { successful: boolean; completed: boolean }) => {
            if (!status.completed) {
              resolve({ cancelled: true });
            }
          },
        });
      });
    },
  });

  return connectPipedreamMutation;
}
